// Tamirat fişleri için HTML üreticileri — TEK KAYNAK.
// Hem Repairs.js (gerçek yazdırma) hem de gizli önizleme sayfası bu fonksiyonları kullanır.
// Böylece önizleme her zaman gerçekte basılan tasarımla birebir aynı kalır.
//
// Not: storeId fiş başlığını (logo/şube/adres) belirler. autoPrint=false verilirse
// barkod yine çizilir ama window.print() tetiklenmez (önizleme için).

import { parseRepairPricing, itemPriceLabel, discountLabel, formatTL } from './repairPricing';

// İndirim varsa "Ara Toplam / İndirim" satırları (toplamdan hemen önce basılır)
const buildPricingSummaryHtml = (repair) => {
  const pr = parseRepairPricing(repair);
  const itemDisc = Number(pr?.item_discount_total) || 0;
  const basketDisc = Number(pr?.discount_amount) || 0;
  if (!(itemDisc + basketDisc > 0)) return '';
  return `
    <div class="row"><span class="label">Ara Toplam:</span><span class="value">${formatTL(pr.subtotal)}</span></div>
    ${itemDisc > 0 ? `<div class="row"><span class="label">Ürün İndirimleri:</span><span class="value">-${formatTL(itemDisc)}</span></div>` : ''}
    ${basketDisc > 0 ? `<div class="row"><span class="label">${pr.mode === 'item' ? 'Sepet' : 'Fiş'} İndirimi${pr.discount_type === 'percent' ? ` (${discountLabel(pr.discount_type, pr.discount_value)})` : ''}:</span><span class="value">-${formatTL(basketDisc)}</span></div>` : ''}
  `;
};

// Kapora ödeme detaylarını parse et (JSON string ya da array gelebilir)
const parseDepositDetails = (repair) => {
  let details = repair?.deposit_payment_details;
  if (typeof details === 'string') {
    try { details = JSON.parse(details); } catch (e) { details = null; }
  }
  return Array.isArray(details) ? details : [];
};

// Tek bir ödeme kaleminin okunabilir tip etiketi: "Nakit", "Kredi Kartı", "Havale Şahıs - X Bank"
const formatPaymentTypeLabel = (p) => {
  if (!p || !p.method) return '';
  if (p.method === 'Havale/EFT' || p.method === 'Havale') {
    return `Havale${p.bank_account ? ' - ' + p.bank_account : ''}`;
  }
  return p.method;
};

// Fişte kapora ödeme tipini gösteren satır(lar)ı HTML olarak üret
// Tek ödeme: "Ödeme Tipi: Nakit" — Çoklu (karışık): her kalem ayrı satır "Havale Şahıs: ₺100"
const buildDepositMethodsHtml = (repair) => {
  const details = parseDepositDetails(repair);
  if (details.length === 0) {
    if (repair?.deposit_payment_method) {
      return `<div class="row"><span class="label">Ödeme Tipi:</span><span class="value">${repair.deposit_payment_method}</span></div>`;
    }
    return '';
  }
  if (details.length === 1) {
    return `<div class="row"><span class="label">Ödeme Tipi:</span><span class="value">${formatPaymentTypeLabel(details[0])}</span></div>`;
  }
  return details.map(p =>
    `<div class="row"><span class="label">${formatPaymentTypeLabel(p)}:</span><span class="value">₺${Number(p.amount).toLocaleString('tr-TR')}</span></div>`
  ).join('');
};

// Ürün listesini parse et + tablo HTML'i üret (iki fiş de aynı yapı)
const buildItems = (repair) => {
  let repairItems = [];
  if (repair.items) {
    try {
      repairItems = typeof repair.items === 'string' ? JSON.parse(repair.items) : repair.items;
    } catch (e) {
      repairItems = [];
    }
  }
  if (!repairItems || repairItems.length === 0) {
    repairItems = [{
      item_type: repair.item_type,
      item_description: repair.item_description,
      problem_description: repair.problem_description
    }];
  }
  const validItems = repairItems.filter(i => i.item_description?.trim());
  // Ürün bazlı fiyatlandırmada her satırın sağında ürün ücreti (indirimliyse eski fiyat üstü çizili)
  const showItemPrices = parseRepairPricing(repair)?.mode === 'item';
  const itemPriceCell = (item) => {
    const label = itemPriceLabel(item);
    if (!showItemPrices || !label) return '';
    const discounted = item.price_type === 'normal' && Number(item.final_price) < Number(item.price);
    return `<td style="padding: 4px 0 4px 6px; text-align: right; vertical-align: top; white-space: nowrap; color: #000;">${discounted ? `<div style="text-decoration: line-through; font-size: 9px;">${formatTL(item.price)}</div>` : ''}${label.toLocaleUpperCase('tr-TR')}</td>`;
  };
  const itemsHtml = `
      <table style="width: 100%; border-collapse: collapse; font-size: 11px; font-weight: bold;">
        ${validItems.map((item, index) => `
          <tr style="border-bottom: 1px solid #ccc;">
            <td style="padding: 4px 0; width: 18px; vertical-align: top; color: #000;">${index + 1}.</td>
            <td style="padding: 4px 0; color: #000;">${item.item_description}${item.problem_description ? ` <span style="color: #555;">- ${item.problem_description}</span>` : ''}</td>
            ${itemPriceCell(item)}
          </tr>
        `).join('')}
      </table>
    `;
  return { validItems, itemsHtml };
};

// Müşteri Teslim Fişi HTML'i
export function buildCustomerReceiptHtml(repair, { storeId, qrCodeData = null, autoPrint = true } = {}) {
  const repairCode = repair.repair_code || String(repair.id).padStart(6, '0'); // 6 basamaklı fiş numarası
  const createdAt = new Date(repair.created_at).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const { validItems, itemsHtml } = buildItems(repair);

  return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Müşteri Teslim Fişi - ${repairCode}</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
        <style>
          @page { size: 80mm auto; margin: 0; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 11px;
            font-weight: 600;
            width: 80mm;
            height: auto;
            margin: 0 auto;
            padding: 3mm 5mm;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            overflow: visible !important;
          }
          .header {
            text-align: center;
            border-bottom: 1px dashed #000;
            padding-bottom: 6px;
            margin-bottom: 6px;
          }
          .logo-img {
            width: 100px;
            height: auto;
            margin-bottom: 4px;
            filter: invert(1);
          }
          .subtitle { font-size: 11px; color: #000; font-weight: bold; }
          .section { margin: 6px 0; }
          .section-title {
            font-weight: bold;
            font-size: 11px;
            color: #000;
            border-bottom: 1px solid #000;
            padding-bottom: 2px;
            margin-bottom: 4px;
          }
          .row {
            display: flex;
            justify-content: space-between;
            padding: 2px 0;
            font-size: 11px;
            color: #000;
          }
          .row .label { color: #000; font-weight: bold; }
          .row .value { font-weight: bold; text-align: right; max-width: 55%; color: #000; }
          .divider { border-top: 1px dashed #000; margin: 4px 0; }
          .amount-box {
            border: 2px solid #000;
            border-radius: 6px;
            padding: 7px 8px;
            margin-top: 6px;
            text-align: center;
          }
          .amount-box .amount-box-label { font-size: 10px; font-weight: bold; color: #000; letter-spacing: 1px; }
          .amount-box .amount-box-value { font-size: 18px; font-weight: bold; color: #000; line-height: 1.2; margin-top: 2px; }
          .footer {
            text-align: center;
            margin-top: 6px;
            padding-top: 6px;
            border-top: 1px dashed #000;
            font-size: 9px;
            color: #000;
            font-weight: bold;
            line-height: 1.4;
          }
          @media print {
            body { width: 80mm; height: auto; overflow: visible !important; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${storeId === 1
            ? `<img src="/Siyah - Beyaz - 1.png" alt="Akik Gold Jewellery" class="logo-img" />`
            : `<div style="margin: 0 auto 2px auto; text-align: center;">
                <img src="/akikfisligi.png" alt="Akik Gümüş" style="width: 110px; height: auto; filter: contrast(3);" />
              </div>`
          }
          ${storeId === 1
            ? `<div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 5px;">Osmangazi Mah. Ahmet Şireci Bulvarı No: 14D<br/>Şehitkamil / Gaziantep<br/>Tel: 0342 230 00 64<br/>www.akikjewellery.com | info@akikjewellery.com</div>`
            : `<div style="font-family: Arial, sans-serif; font-size: 11px; font-weight: bold; color: #000; margin-bottom: 3px;">${storeId === 3 ? '(YEŞİLSU ŞUBESİ)' : '(OSMANGAZİ ŞUBESİ)'}</div>
               <div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 5px;">${storeId === 3 ? 'Bey Mah. Atatürk Blv. 43/A<br/>Şahinbey / Gaziantep' : 'Osmangazi Mah. Ahmet Şireci Bulvarı No: 16/F<br/>Şehitkamil / Gaziantep'}<br/>Tel: 0342 230 00 64</div>`
          }
          <div class="subtitle">Tamirat Fişi</div>
        </div>

        <div style="display: flex; gap: 6px; align-items: flex-start; margin: 5px 0; border: 1px solid #000; border-radius: 4px; padding: 5px;">
          <div style="flex-shrink: 0; text-align: center;">
            <div style="font-size: 9px; font-weight: bold; color: #000;">FİŞ NO</div>
            <div style="font-size: 16px; font-weight: bold; letter-spacing: 2px; color: #000;">${repairCode}</div>
            <div style="margin: 2px 0 0 0;"><svg id="barcode-receipt"></svg></div>
          </div>
          <div style="flex: 1; border-left: 1px dashed #000; padding-left: 6px; font-weight: bold; color: #000;">
            <div style="font-size: 12px; margin-bottom: 2px;">${repair.customer_name}</div>
            <div style="font-size: 12px; margin-bottom: 2px;">${repair.customer_phone || '-'}</div>
            <div style="font-size: 12px; margin-bottom: 2px;">${createdAt}</div>
            ${repair.staff_name ? `<div style="font-size: 12px;">Kasiyer: ${repair.staff_name.split(' ').map(word => word.charAt(0).toUpperCase() + '***').join(' ')}</div>` : ''}
          </div>
        </div>

        <div class="section">
          <div class="section-title">ÜRÜNLER (${validItems.length} adet)</div>
          ${itemsHtml}
        </div>

        ${repair.price_type === 'free' ? `
        <div class="section">
          <div class="section-title">ÜCRET BİLGİLERİ</div>
          <div class="amount-box">
            <div class="amount-box-label">ÜCRET</div>
            <div class="amount-box-value">ÜCRETSİZ</div>
          </div>
        </div>
        ` : repair.price_type === 'pending' ? `
        <div class="section">
          <div class="section-title">ÜCRET BİLGİLERİ</div>
          <div class="amount-box">
            <div class="amount-box-label">ÜCRET</div>
            <div class="amount-box-value">HESAPLANACAK</div>
          </div>
        </div>
        ` : repair.estimated_price > 0 ? `
        <div class="section">
          <div class="section-title">ÜCRET BİLGİLERİ</div>
          ${buildPricingSummaryHtml(repair)}
          ${repair.deposit_amount > 0 ? `
          <div class="divider"></div>
          <div style="text-align: center; font-weight: bold; font-size: 13px; color: #000; padding: 2px 0;">Toplam: ₺${Number(repair.estimated_price).toLocaleString('tr-TR')}</div>
          <div class="divider"></div>
          ${buildDepositMethodsHtml(repair)}
          <div class="amount-box">
            <div class="amount-box-label">KALAN TUTAR</div>
            <div class="amount-box-value">₺${Number(repair.estimated_price - repair.deposit_amount).toLocaleString('tr-TR')}</div>
          </div>
          ` : `
          <div class="amount-box">
            <div class="amount-box-label">TUTAR</div>
            <div class="amount-box-value">₺${Number(repair.estimated_price).toLocaleString('tr-TR')}</div>
          </div>
          `}
        </div>
        ` : ''}

        ${repair.notes ? `
        <div class="section">
          <div class="section-title">NOTLAR</div>
          <div style="font-size: 11px; padding: 2px 0; color: #000; font-weight: bold;">${repair.notes}</div>
        </div>
        ` : ''}

        ${qrCodeData ? `
        <div style="display: flex; align-items: center; gap: 8px; margin: 6px 0; padding: 4px 0;">
          <img src="${qrCodeData}" alt="QR Kod" style="width: 50px; height: 50px; flex-shrink: 0;" />
          <div style="font-size: 9px; color: #000; line-height: 1.4;">Tamirat durumunuzu takip etmek için QR kodu okutabilirsiniz.</div>
        </div>
        ` : ''}

        <div class="footer">
          <div>TEŞEKKÜR EDERİZ YİNE BEKLERİZ</div>
          <div>BİLGİ FİŞİDİR MALİ DEĞERİ YOKTUR</div>
          <div style="margin-top: 2px;">15 GÜN İÇERİSİNDE TESLİM ALINMAYAN ÜRÜNLERDEN FİRMAMIZ SORUMLU DEĞİLDİR</div>
          <div style="margin-top: 2px;">ÜRÜNLERİNİZİ YALNIZCA BU FİŞLE TESLİM ALABİLİRSİNİZ FİŞİNİZİ SAKLAYINIZ</div>
        </div>

        <script>
          window.onload = function() {
            try {
              JsBarcode("#barcode-receipt", "${repairCode}", {
                format: "CODE128", width: 1.2, height: 30,
                displayValue: false, margin: 0, background: "transparent"
              });
            } catch(e) { console.error('Barkod hatası:', e); }
            ${autoPrint ? `
            var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
            var printed = false;
            function doPrint() {
              if (printed) return;
              printed = true;
              window.print();
              if (directPrint) { setTimeout(function() { window.close(); }, 1500); }
            }
            var imgs = document.querySelectorAll('img');
            var loaded = 0;
            var total = imgs.length;
            if (total === 0) { setTimeout(doPrint, 800); }
            else {
              function check() { loaded++; if (loaded >= total) setTimeout(doPrint, 400); }
              imgs.forEach(function(img) {
                if (img.complete) { check(); }
                else { img.onload = check; img.onerror = check; }
              });
              setTimeout(doPrint, 4000);
            }
            ` : ''}
          }
        </script>
      </body>
      </html>
    `;
}

// Teslimden önce alınan tamirat ödemeleri + teslimde alınan ödemeyi birleştirip
// "Tamirat Ödemesi - Nakit: ₺100" gibi satırlar üretir. "Kapora" kavramı yok — teslimden
// önce (kayıt anında veya sonrasında istenildiği an) alınan her tutar doğrudan tamirat
// ödemesi/ücretidir. Her iki kalem de varsa önek eklenir, tek kalem varsa öneksiz gösterilir.
const buildPaymentBreakdownRows = (repair) => {
  const depositDetails = parseDepositDetails(repair);
  const deposit = Number(repair.deposit_amount) || 0;

  let paymentDetails = repair.payment_details;
  if (typeof paymentDetails === 'string') {
    try { paymentDetails = JSON.parse(paymentDetails); } catch (e) { paymentDetails = null; }
  }
  if (!Array.isArray(paymentDetails)) paymentDetails = [];
  const delivery = Number(repair.payment_amount) || 0;

  const showPrefix = deposit > 0 && delivery > 0;
  const rows = [];

  if (deposit > 0) {
    const prefix = showPrefix ? 'Tamirat Ödemesi - ' : '';
    if (depositDetails.length > 1) {
      depositDetails.forEach(p => rows.push(
        `<div class="row"><span class="label">${prefix}${formatPaymentTypeLabel(p)}:</span><span class="value">₺${Number(p.amount).toLocaleString('tr-TR')}</span></div>`
      ));
    } else {
      const label = depositDetails[0] ? formatPaymentTypeLabel(depositDetails[0]) : (repair.deposit_payment_method || 'Ödeme');
      rows.push(`<div class="row"><span class="label">${prefix}${label}:</span><span class="value">₺${deposit.toLocaleString('tr-TR')}</span></div>`);
    }
  }

  if (delivery > 0) {
    const prefix = showPrefix ? 'Teslim Ödemesi - ' : '';
    if (paymentDetails.length > 1) {
      paymentDetails.forEach(p => rows.push(
        `<div class="row"><span class="label">${prefix}${formatPaymentTypeLabel(p)}:</span><span class="value">₺${Number(p.amount).toLocaleString('tr-TR')}</span></div>`
      ));
    } else {
      const label = paymentDetails[0] ? formatPaymentTypeLabel(paymentDetails[0]) : (repair.payment_method || 'Ödeme');
      rows.push(`<div class="row"><span class="label">${prefix}${label}:</span><span class="value">₺${delivery.toLocaleString('tr-TR')}</span></div>`);
    }
  }

  return rows.join('');
};

// Tahsilat / Ödeme Bilgi Fişi — tamirat için o ana kadar alınan tutar ve kalan tutarı gösterir.
// Kapora alırken, teslimde ödeme alırken veya herhangi bir zamanda tamirat detayından yazdırılabilir.
export function buildRepairPaymentReceiptHtml(repair, { storeId, autoPrint = true } = {}) {
  const repairCode = repair.repair_code || String(repair.id).padStart(6, '0');
  const now = new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const { validItems, itemsHtml } = buildItems(repair);

  const totalPrice = Number(repair.estimated_price) || 0;
  const collected = (Number(repair.deposit_amount) || 0) + (Number(repair.payment_amount) || 0);
  const remaining = Math.max(0, totalPrice - collected);
  const isFullyPaid = totalPrice > 0 && remaining <= 0.01;
  const paymentRowsHtml = buildPaymentBreakdownRows(repair);

  return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Tamirat Tahsilat Fişi - ${repairCode}</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
        <style>
          @page { size: 80mm auto; margin: 0; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 11px;
            font-weight: 600;
            width: 80mm;
            height: auto;
            margin: 0 auto;
            padding: 3mm 5mm;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            overflow: visible !important;
          }
          .header {
            text-align: center;
            border-bottom: 1px dashed #000;
            padding-bottom: 6px;
            margin-bottom: 6px;
          }
          .logo-img {
            width: 100px;
            height: auto;
            margin-bottom: 4px;
            filter: invert(1);
          }
          .subtitle { font-size: 11px; color: #000; font-weight: bold; }
          .section { margin: 6px 0; }
          .section-title {
            font-weight: bold;
            font-size: 11px;
            color: #000;
            border-bottom: 1px solid #000;
            padding-bottom: 2px;
            margin-bottom: 4px;
          }
          .row {
            display: flex;
            justify-content: space-between;
            padding: 2px 0;
            font-size: 11px;
            color: #000;
          }
          .row .label { color: #000; font-weight: bold; }
          .row .value { font-weight: bold; text-align: right; max-width: 55%; color: #000; }
          .divider { border-top: 1px dashed #000; margin: 4px 0; }
          .grand-total {
            text-align: center;
            font-weight: bold;
            font-size: 14px;
            color: #000;
            padding: 4px 0;
            border-top: 2px solid #000;
            border-bottom: 2px solid #000;
            margin: 6px 0;
          }
          .amount-box {
            border: 2px solid #000;
            border-radius: 6px;
            padding: 7px 8px;
            margin-top: 6px;
            text-align: center;
          }
          .amount-box .amount-box-label { font-size: 10px; font-weight: bold; color: #000; letter-spacing: 1px; }
          .amount-box .amount-box-value { font-size: 18px; font-weight: bold; color: #000; line-height: 1.2; margin-top: 2px; }
          .footer {
            text-align: center;
            margin-top: 6px;
            padding-top: 6px;
            border-top: 1px dashed #000;
            font-size: 9px;
            color: #000;
            font-weight: bold;
            line-height: 1.4;
          }
          @media print {
            body { width: 80mm; height: auto; overflow: visible !important; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${storeId === 1
            ? `<img src="/Siyah - Beyaz - 1.png" alt="Akik Gold Jewellery" class="logo-img" />`
            : `<div style="margin: 0 auto 2px auto; text-align: center;">
                <img src="/akikfisligi.png" alt="Akik Gümüş" style="width: 110px; height: auto; filter: contrast(3);" />
              </div>`
          }
          ${storeId === 1
            ? `<div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 5px;">Osmangazi Mah. Ahmet Şireci Bulvarı No: 14D<br/>Şehitkamil / Gaziantep<br/>Tel: 0342 230 00 64<br/>www.akikjewellery.com | info@akikjewellery.com</div>`
            : `<div style="font-family: Arial, sans-serif; font-size: 11px; font-weight: bold; color: #000; margin-bottom: 3px;">${storeId === 3 ? '(YEŞİLSU ŞUBESİ)' : '(OSMANGAZİ ŞUBESİ)'}</div>
               <div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 5px;">${storeId === 3 ? 'Bey Mah. Atatürk Blv. 43/A<br/>Şahinbey / Gaziantep' : 'Osmangazi Mah. Ahmet Şireci Bulvarı No: 16/F<br/>Şehitkamil / Gaziantep'}<br/>Tel: 0342 230 00 64</div>`
          }
          <div class="subtitle">Tamirat Tahsilat Fişi</div>
        </div>

        <div style="display: flex; gap: 6px; align-items: flex-start; margin: 5px 0; border: 1px solid #000; border-radius: 4px; padding: 5px;">
          <div style="flex-shrink: 0; text-align: center;">
            <div style="font-size: 9px; font-weight: bold; color: #000;">FİŞ NO</div>
            <div style="font-size: 16px; font-weight: bold; letter-spacing: 2px; color: #000;">${repairCode}</div>
            <div style="margin: 2px 0 0 0;"><svg id="barcode-payment-receipt"></svg></div>
          </div>
          <div style="flex: 1; border-left: 1px dashed #000; padding-left: 6px; font-weight: bold; color: #000;">
            <div style="font-size: 12px; margin-bottom: 2px;">${repair.customer_name}</div>
            <div style="font-size: 12px; margin-bottom: 2px;">${repair.customer_phone || '-'}</div>
            <div style="font-size: 12px; margin-bottom: 2px;">${now}</div>
            ${repair.staff_name ? `<div style="font-size: 12px;">Kasiyer: ${repair.staff_name.split(' ').map(word => word.charAt(0).toUpperCase() + '***').join(' ')}</div>` : ''}
          </div>
        </div>

        <div class="section">
          <div class="section-title">ÜRÜNLER (${validItems.length} adet)</div>
          ${itemsHtml}
        </div>

        <div class="section">
          <div class="section-title">ÖDEME DURUMU</div>
          ${buildPricingSummaryHtml(repair)}
          <div class="row"><span class="label">Toplam Ücret:</span><span class="value">₺${totalPrice.toLocaleString('tr-TR')}</span></div>
          ${paymentRowsHtml}
          <div class="grand-total">ALINAN TOPLAM: ₺${collected.toLocaleString('tr-TR')}</div>
          ${isFullyPaid ? `
          <div class="amount-box">
            <div class="amount-box-label">ÖDEME DURUMU</div>
            <div class="amount-box-value">TAMAMEN ÖDENDİ ✓</div>
          </div>
          ` : `
          <div class="amount-box">
            <div class="amount-box-label">KALAN TUTAR</div>
            <div class="amount-box-value">₺${remaining.toLocaleString('tr-TR')}</div>
          </div>
          `}
        </div>

        ${repair.notes ? `
        <div class="section">
          <div class="section-title">NOTLAR</div>
          <div style="font-size: 11px; padding: 2px 0; color: #000; font-weight: bold;">${repair.notes}</div>
        </div>
        ` : ''}

        <div class="footer">
          <div>ÖDEMENİZ İÇİN TEŞEKKÜR EDERİZ</div>
          <div>BİLGİ FİŞİDİR MALİ DEĞERİ YOKTUR</div>
        </div>

        <script>
          window.onload = function() {
            try {
              JsBarcode("#barcode-payment-receipt", "${repairCode}", {
                format: "CODE128", width: 1.2, height: 30,
                displayValue: false, margin: 0, background: "transparent"
              });
            } catch(e) { console.error('Barkod hatası:', e); }
            ${autoPrint ? `
            var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
            var printed = false;
            function doPrint() {
              if (printed) return;
              printed = true;
              window.print();
              if (directPrint) { setTimeout(function() { window.close(); }, 1500); }
            }
            var imgs = document.querySelectorAll('img');
            var loaded = 0;
            var total = imgs.length;
            if (total === 0) { setTimeout(doPrint, 800); }
            else {
              function check() { loaded++; if (loaded >= total) setTimeout(doPrint, 400); }
              imgs.forEach(function(img) {
                if (img.complete) { check(); }
                else { img.onload = check; img.onerror = check; }
              });
              setTimeout(doPrint, 4000);
            }
            ` : ''}
          }
        </script>
      </body>
      </html>
    `;
}

// Tamirat Fişi HTML'i (iç kullanım)
export function buildRepairReceiptHtml(repair, { storeId, autoPrint = true } = {}) {
  const repairCode = repair.repair_code || String(repair.id).padStart(6, '0');
  const createdAt = new Date(repair.created_at).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const deadline = repair.deadline
    ? new Date(repair.deadline).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })
    : '-';
  const { validItems, itemsHtml } = buildItems(repair);

  return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Tamirat Fişi - ${repairCode}</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 12px;
            font-weight: 600;
            width: 80mm;
            margin: 0 auto;
            padding: 5mm;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
            margin-bottom: 15px;
          }
          .header-title {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 5px;
            padding: 5px;
          }
          .barcode-section {
            text-align: center;
            padding: 10px 5px;
            margin: 10px 0;
            border: 1px solid #333;
          }
          .barcode-svg svg {
            display: block;
            margin: 0 auto;
          }
          .barcode-text {
            font-size: 14px;
            font-weight: bold;
            margin-top: 5px;
            letter-spacing: 2px;
          }
          .info-section {
            margin: 15px 0;
            padding: 10px;
            border-left: 3px solid #000;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            font-size: 13px;
            color: #000;
            font-weight: bold;
          }
          .info-row .label { font-weight: bold; color: #000; }
          .info-row .value { font-weight: bold; color: #000; }
          .items-section {
            margin: 15px 0;
          }
          .section-title {
            font-weight: bold;
            font-size: 13px;
            color: #000;
            border-bottom: 2px solid #000;
            padding-bottom: 3px;
            margin-bottom: 10px;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
            font-weight: bold;
          }
          .items-table th {
            background: #ddd;
            color: #000;
            padding: 6px 4px;
            text-align: left;
            font-size: 11px;
            font-weight: bold;
            border: 1px solid #000;
          }
          .items-table td {
            font-weight: bold;
            color: #000;
            padding: 8px;
            border: 1px solid #000;
          }
          .item-count {
            text-align: center;
            font-size: 14px;
            font-weight: bold;
            color: #000;
            padding: 8px;
            border: 2px solid #000;
            margin: 10px 0;
          }
          .section { margin: 15px 0; }
          .row {
            display: flex;
            justify-content: space-between;
            padding: 3px 0;
            font-size: 12px;
            color: #000;
            font-weight: bold;
          }
          .row .label { font-weight: bold; color: #000; }
          .row .value { font-weight: bold; color: #000; text-align: right; }
          .divider { border-top: 1px dashed #000; margin: 4px 0; }
          .amount-box {
            border: 2px solid #000;
            border-radius: 6px;
            padding: 8px;
            margin-top: 6px;
            text-align: center;
          }
          .amount-box .amount-box-label { font-size: 11px; font-weight: bold; color: #000; letter-spacing: 1px; }
          .amount-box .amount-box-value { font-size: 19px; font-weight: bold; color: #000; line-height: 1.2; margin-top: 2px; }
          .footer {
            text-align: center;
            margin-top: 15px;
            padding-top: 10px;
            border-top: 2px dashed #000;
            font-size: 11px;
            color: #000;
            font-weight: bold;
          }
          @media print {
            body { width: 80mm; }
          }
        </style>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
      </head>
      <body>
        <div class="header">
          <div class="header-title">TAMİRAT FİŞİ</div>
        </div>

        <div style="display: flex; gap: 6px; align-items: flex-start; margin: 5px 0; border: 1px solid #000; border-radius: 4px; padding: 5px;">
          <div style="flex-shrink: 0; text-align: center;">
            <div style="font-size: 9px; font-weight: bold; color: #000;">FİŞ NO</div>
            <div style="font-size: 16px; font-weight: bold; letter-spacing: 2px; color: #000;">${repairCode}</div>
            <div style="margin: 2px 0 0 0;"><svg id="barcode-repair"></svg></div>
          </div>
          <div style="flex: 1; border-left: 1px dashed #000; padding-left: 6px; font-weight: bold; color: #000;">
            <div style="font-size: 12px; margin-bottom: 2px;">${repair.customer_name}</div>
            <div style="font-size: 12px; margin-bottom: 2px;">${repair.customer_phone || '-'}</div>
            <div style="font-size: 12px; margin-bottom: 2px;">${createdAt}</div>
            ${repair.staff_name ? `<div style="font-size: 12px;">Kasiyer: ${repair.staff_name.split(' ').map(word => word.charAt(0).toUpperCase() + '***').join(' ')}</div>` : ''}
          </div>
        </div>

        <div class="section">
          <div class="section-title">ÜRÜNLER (${validItems.length} adet)</div>
          ${itemsHtml}
        </div>

        <div class="section" style="margin-top: 8px;">
          <div style="border-bottom: 2px solid #000; margin-bottom: 10px;"></div>
          <div class="row"><span class="label">En Geç Teslim:</span><span class="value">${deadline}</span></div>
          <div style="border-bottom: 2px solid #000; margin-top: 10px;"></div>
        </div>

        ${repair.price_type === 'free' ? `
        <div class="section" style="margin-top: 8px;">
          <div class="section-title">ÜCRET BİLGİLERİ</div>
          <div class="amount-box"><div class="amount-box-label">ÜCRET</div><div class="amount-box-value">ÜCRETSİZ</div></div>
        </div>
        ` : repair.price_type === 'pending' ? `
        <div class="section" style="margin-top: 8px;">
          <div class="section-title">ÜCRET BİLGİLERİ</div>
          <div class="amount-box"><div class="amount-box-label">ÜCRET</div><div class="amount-box-value">HESAPLANACAK</div></div>
        </div>
        ` : (Number(repair.estimated_price) > 0 || Number(repair.deposit_amount) > 0) ? `
        <div class="section" style="margin-top: 8px;">
          <div class="section-title">ÜCRET BİLGİLERİ</div>
          ${Number(repair.estimated_price) > 0 ? buildPricingSummaryHtml(repair) : ''}
          ${Number(repair.estimated_price) > 0 && Number(repair.deposit_amount) > 0 ? `
            <div class="divider"></div>
            <div style="text-align: center; font-weight: bold; font-size: 13px; color: #000; padding: 2px 0;">Toplam: ₺${Number(repair.estimated_price).toLocaleString('tr-TR')}</div>
            <div class="divider"></div>
            ${buildDepositMethodsHtml(repair)}
            <div class="amount-box"><div class="amount-box-label">KALAN TUTAR</div><div class="amount-box-value">₺${(Number(repair.estimated_price) - Number(repair.deposit_amount)).toLocaleString('tr-TR')}</div></div>
          ` : Number(repair.estimated_price) > 0 ? `
            <div class="amount-box"><div class="amount-box-label">TUTAR</div><div class="amount-box-value">₺${Number(repair.estimated_price).toLocaleString('tr-TR')}</div></div>
          ` : `
            <div class="amount-box"><div class="amount-box-label">ALINAN ÜCRET</div><div class="amount-box-value">₺${Number(repair.deposit_amount).toLocaleString('tr-TR')}</div></div>
            ${buildDepositMethodsHtml(repair)}
          `}
        </div>
        ` : ''}

        ${repair.notes ? `
        <div class="section" style="margin-top: 8px;">
          <div class="section-title">NOTLAR</div>
          <div style="font-size: 11px; padding: 2px 0; color: #000; font-weight: bold;">${repair.notes}</div>
        </div>
        ` : ''}

        <div class="footer">
          <div style="font-size: 12px; font-weight: bold;">${storeId === 1 ? 'AKİK GOLD' : storeId === 3 ? 'AKİK YEŞİLSU' : 'AKİK OSMANGAZİ'}</div>
          <div style="margin-top: 4px;">Bu fiş tamirat takibi için kullanılır.</div>
          <div style="margin-top: 5px;">Yazdırma: ${new Date().toLocaleString('tr-TR')}</div>
        </div>

        <script>
          window.onload = function() {
            try {
              JsBarcode("#barcode-repair", "${repairCode}", {
                format: "CODE128", width: 1.2, height: 30,
                displayValue: false, margin: 0, background: "transparent"
              });
            } catch(e) { console.error('Barkod hatası:', e); }
            ${autoPrint ? `
            var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
            var printed = false;
            function doPrint() {
              if (printed) return;
              printed = true;
              window.print();
              if (directPrint) { setTimeout(function() { window.close(); }, 1500); }
            }
            var imgs = document.querySelectorAll('img');
            var loaded = 0;
            var total = imgs.length;
            if (total === 0) { setTimeout(doPrint, 800); }
            else {
              function check() { loaded++; if (loaded >= total) setTimeout(doPrint, 400); }
              imgs.forEach(function(img) {
                if (img.complete) { check(); }
                else { img.onload = check; img.onerror = check; }
              });
              setTimeout(doPrint, 4000);
            }
            ` : ''}
          }
        </script>
      </body>
      </html>
    `;
}
